import React, { useEffect, useState } from "react";
import './AdminDraftReview.css';
import SecondaryFooter from '../Common/SecondaryFooter';
import config from '../../environment/config';
import AdminLayout from '../Common/AdminLayout';

const AdminDraftReview = () => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [approvedDraftId, setApprovedDraftId] = useState(null);

    // Fetch drafts on component mount
    useEffect(() => {
        fetchDrafts();
    }, []);

    const fetchDrafts = async () => {
        setLoading(true);
        setError(null);

        try {
            const API_BASE_URL = config.API_URL;

            const token = localStorage.getItem('token') ||
                localStorage.getItem('jhi-authenticationToken') ||
                localStorage.getItem('authToken') ||
                sessionStorage.getItem('jhi-authenticationToken');

            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('📥 Fetching drafts with publish_flag = N...');

            // Fetch products where publish_flag = 'N' (Draft status)
            const response = await fetch(`${API_BASE_URL}/api/seo-product-details?publishFlag=N`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error('Failed to fetch drafts');
            }

            const productsData = await response.json();

            console.log(`✅ Found ${productsData.length} draft products`);

            // For each product, fetch its features, plans, specifications, and support
            const draftsWithDetails = await Promise.all(
                productsData.map(async (product) => {
                    // Fetch features for this product
                    let features = [];
                    try {
                        const featuresResponse = await fetch(
                            `${API_BASE_URL}/api/seo-product-features?productId=${product.productId}`,
                            { headers }
                        );
                        if (featuresResponse.ok) {
                            features = await featuresResponse.json();
                        } else {
                            console.warn(`Failed to fetch features for product ${product.productId}: ${featuresResponse.status}`);
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch features for product ${product.productId}:`, err);
                    }

                    // Fetch plans for this product
                    let plans = [];
                    try {
                        const plansResponse = await fetch(
                            `${API_BASE_URL}/api/seo-product-plans?productId=${product.productId}`,
                            { headers }
                        );
                        if (plansResponse.ok) {
                            plans = await plansResponse.json();
                        } else {
                            console.warn(`Failed to fetch plans for product ${product.productId}: ${plansResponse.status}`);
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch plans for product ${product.productId}:`, err);
                    }

                    // Fetch specifications for this product
                    let specifications = [];
                    try {
                        const specificationsResponse = await fetch(
                            `${API_BASE_URL}/api/seo-product-specifications?productId=${product.productId}`,
                            { headers }
                        );
                        if (specificationsResponse.ok) {
                            specifications = await specificationsResponse.json();
                        } else {
                            console.warn(`Failed to fetch specifications for product ${product.productId}: ${specificationsResponse.status}`);
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch specifications for product ${product.productId}:`, err);
                    }

                    // Fetch support for this product
                    let support = [];
                    try {
                        const supportResponse = await fetch(
                            `${API_BASE_URL}/api/seo-product-supports/product/${product.productId}`,
                            { headers }
                        );
                        if (supportResponse.ok) {
                            support = await supportResponse.json();
                        } else {
                            console.warn(`Failed to fetch support for product ${product.productId}: ${supportResponse.status}`);
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch support for product ${product.productId}:`, err);
                    }

                    return {
                        id: product.productId,
                        title: product.productName || 'Untitled',
                        contentType: 'Product',
                        author: product.createdBy || 'Unknown',
                        createdDate: product.createdDate || new Date().toISOString(),
                        lastUpdated: product.modifiedDate || product.createdDate || new Date().toISOString(),
                        modifiedBy: product.modifiedBy || product.createdBy || 'Unknown',
                        shortDescription: product.productDescription || '',
                        fullContent: product.productOverview || '',
                        highlights: product.productHighlights || '',
                        categoryId: product.categoryId,
                        metaTitle: product.productName || '',
                        metaDescription: product.productDescription || '',
                        // Store complete data for approval
                        completeData: {
                            ...product,
                            features: features,
                            plans: plans,
                            specifications: specifications,
                            support: support
                        }
                    };
                })
            );

            setDrafts(draftsWithDetails);

            // Set first draft as selected by default
            if (draftsWithDetails.length > 0) {
                setSelectedDraft(draftsWithDetails[0]);
            } else {
                setSelectedDraft(null);
            }

        } catch (error) {
            console.error('❌ Error fetching drafts:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDraftClick = (draft) => {
        setSelectedDraft(draft);
    };

    const handleApprove = async () => {
        if (!selectedDraft) {
            alert('No draft selected');
            return;
        }

        setLoading(true);

        try {
            const API_BASE_URL = config.API_URL;

            const token = localStorage.getItem('token') ||
                localStorage.getItem('jhi-authenticationToken') ||
                localStorage.getItem('authToken') ||
                sessionStorage.getItem('jhi-authenticationToken');

            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const productId = selectedDraft.id;
            const productData = selectedDraft.completeData;

            console.log('✅ Approving draft with product_id:', productId);
            console.log('📦 Current product data:', productData);

            // Get current user info
            const userDataStr = localStorage.getItem('userData');
            const userData = userDataStr ? JSON.parse(userDataStr) : null;
            const username = userData?.username || 'Admin';

            // First, fetch the current product to get all fields
            const getCurrentResponse = await fetch(`${API_BASE_URL}/api/seo-product-details/${productId}`, {
                method: 'GET',
                headers: headers
            });

            if (!getCurrentResponse.ok) {
                throw new Error('Failed to fetch current product data');
            }

            const currentProduct = await getCurrentResponse.json();
            console.log('📥 Current product from API:', currentProduct);

            // Update payload - preserve all existing fields and only change what's needed
            const updatePayload = {
                ...currentProduct,
                isActive: 1,
                publishFlag: 'Y',
                modifiedBy: username,
                modifiedDate: new Date().toISOString()
            };

            console.log('📤 Sending approval request with PATCH method:', updatePayload);

            // Using PATCH to use partialUpdate method
            const updateResponse = await fetch(`${API_BASE_URL}/api/seo-product-details/${productId}`, {
                method: 'PATCH',
                headers: headers,
                body: JSON.stringify(updatePayload)
            });

            if (!updateResponse.ok) {
                const errorText = await updateResponse.text();
                console.error('❌ Server response:', errorText);
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    errorData = { message: errorText };
                }
                throw new Error(errorData?.message || errorData?.title || `Failed to approve draft (${updateResponse.status})`);
            }

            const responseData = await updateResponse.json();
            console.log('✅ Product approved and published!', responseData);

            // Verify publishFlag was saved
            if (responseData.publishFlag === 'Y') {
                console.log('✅ publishFlag successfully set to Y');
            } else {
                console.warn('⚠️ WARNING: publishFlag was not saved correctly!', {
                    sent: 'Y',
                    received: responseData.publishFlag
                });
            }

            // Activate all features using PATCH
            const features = productData.features || [];
            for (const feature of features) {
                try {
                    const getFeatureResponse = await fetch(
                        `${API_BASE_URL}/api/seo-product-features/${feature.featureId}`,
                        { method: 'GET', headers }
                    );

                    if (getFeatureResponse.ok) {
                        const currentFeature = await getFeatureResponse.json();

                        await fetch(`${API_BASE_URL}/api/seo-product-features/${feature.featureId}`, {
                            method: 'PATCH',
                            headers: headers,
                            body: JSON.stringify({
                                ...currentFeature,
                                isActive: 1
                            })
                        });
                        console.log(`✅ Activated feature: ${feature.featureName}`);
                    }
                } catch (err) {
                    console.warn('Failed to activate feature:', err);
                }
            }

            // Activate all plans using PATCH
            const plans = productData.plans || [];
            for (const plan of plans) {
                try {
                    const getPlanResponse = await fetch(
                        `${API_BASE_URL}/api/seo-product-plans/${plan.planId}`,
                        { method: 'GET', headers }
                    );

                    if (getPlanResponse.ok) {
                        const currentPlan = await getPlanResponse.json();

                        await fetch(`${API_BASE_URL}/api/seo-product-plans/${plan.planId}`, {
                            method: 'PATCH',
                            headers: headers,
                            body: JSON.stringify({
                                ...currentPlan,
                                isActive: 1
                            })
                        });
                        console.log(`✅ Activated plan: ${plan.planName}`);
                    }
                } catch (err) {
                    console.warn('Failed to activate plan:', err);
                }
            }

            // Activate all specifications using PATCH
            const specifications = productData.specifications || [];
            for (const spec of specifications) {
                try {
                    const getSpecResponse = await fetch(
                        `${API_BASE_URL}/api/seo-product-specifications/${spec.specificationId}`,
                        { method: 'GET', headers }
                    );

                    if (getSpecResponse.ok) {
                        const currentSpec = await getSpecResponse.json();

                        await fetch(`${API_BASE_URL}/api/seo-product-specifications/${spec.specificationId}`, {
                            method: 'PATCH',
                            headers: headers,
                            body: JSON.stringify({
                                ...currentSpec,
                                isActive: 1
                            })
                        });
                        console.log(`✅ Activated specification: ${spec.specificationName}`);
                    }
                } catch (err) {
                    console.warn('Failed to activate specification:', err);
                }
            }

            // Activate all support entries using PATCH
            const support = productData.support || [];
            for (const supportItem of support) {
                try {
                    const getSupportResponse = await fetch(
                        `${API_BASE_URL}/api/seo-product-supports/${supportItem.supportId}`,
                        { method: 'GET', headers }
                    );

                    if (getSupportResponse.ok) {
                        const currentSupport = await getSupportResponse.json();

                        await fetch(`${API_BASE_URL}/api/seo-product-supports/${supportItem.supportId}`, {  
                            method: 'PATCH',
                            headers: headers,
                            body: JSON.stringify({
                                ...currentSupport,
                                isActive: 1
                            })
                        });
                        console.log(`✅ Activated support: ${supportItem.supportType}`);
                    }
                } catch (err) {
                    console.warn('Failed to activate support:', err);
                }
            }

            setShowConfirmation(true);
            setApprovedDraftId(selectedDraft.id);

            // Refresh drafts list (approved draft will disappear)
            setTimeout(() => {
                fetchDrafts();
                setShowConfirmation(false);
                setApprovedDraftId(null);
            }, 3000);

        } catch (error) {
            console.error('❌ Error approving draft:', error);
            alert(`Failed to approve draft: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Helper functions
    const getCategoryName = (categoryId) => {
        const categoryMap = {
            1: 'SEO Tools',
            2: 'E-Learning',
            3: 'Talent Acquisition',
            4: 'Workflow Automation',
            5: 'Digital Marketing',
            6: 'Analytics & Reporting'
        };
        return categoryMap[categoryId] || 'Unknown Category';
    };

    const getContentTypeIcon = (type) => {
        const icons = {
            'Product': '📦',
            'Feature': '⚡',
            'Plan': '💰',
            'Page': '📄'
        };
        return icons[type] || '📝';
    };

    const getContentTypeColor = (type) => {
        const colors = {
            'Product': 'type-product',
            'Feature': 'type-feature',
            'Plan': 'type-plan',
            'Page': 'type-page'
        };
        return colors[type] || 'type-default';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AdminLayout>
        <div className="admin-draft-review-page">
            {/* Page Header */}
            <div className="page-header">
                <div className="header-content">
                    <div className="header-badge">Admin Panel</div>
                    <h1 className="header-title">Draft Review</h1>
                    <p className="header-subtitle">Review and approve content before publishing</p>
                </div>
            </div>

            {/* Confirmation Toast */}
            {showConfirmation && (
                <div className="confirmation-toast">
                    <div className="toast-icon">✅</div>
                    <div className="toast-message">
                        <strong>Draft Approved!</strong>
                        <p>"{selectedDraft?.title}" has been published successfully.</p>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="content-wrapper">
                <div className="content-container">
                    {/* Draft List Section */}
                    <div className="draft-list-section">
                        <div className="section-header">
                            <h2 className="section-title">Pending Drafts</h2>
                            <span className="draft-count">{drafts.length} items</span>
                        </div>

                        {/* Loading */}
                        {loading && (
                            <div className="loading-overlay">
                                <div className="loading-spinner">Loading drafts...</div>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="error-message">
                                <p>❌ Error: {error}</p>
                                <button onClick={fetchDrafts}>Retry</button>
                            </div>
                        )}

                        {/* Empty */}
                        {!loading && !error && drafts.length === 0 && (
                            <div className="empty-state">
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                                <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No pending drafts</p>
                                <p style={{ fontSize: '14px', color: '#666' }}>All drafts have been reviewed and approved.</p>
                            </div>
                        )}

                        {/* Draft List */}
                        {!loading && !error && drafts.length > 0 && (
                            <div className="draft-list">
                                {drafts.map((draft) => (
                                    <div
                                        key={draft.id}
                                        className={`draft-card ${selectedDraft?.id === draft.id ? 'active' : ''} ${approvedDraftId === draft.id ? 'approved' : ''}`}
                                        onClick={() => handleDraftClick(draft)}
                                    >
                                        <div className="draft-card-header">
                                            <div className={`content-type-badge ${getContentTypeColor(draft.contentType)}`}>
                                                <span className="type-icon">{getContentTypeIcon(draft.contentType)}</span>
                                                <span className="type-label">{draft.contentType}</span>
                                            </div>
                                            {approvedDraftId === draft.id && (
                                                <div className="approved-badge">✓ Approved</div>
                                            )}
                                        </div>

                                        <h3 className="draft-title">{draft.title}</h3>

                                        <div className="draft-meta">
                                            <div className="meta-item">
                                                <span className="meta-icon">👤</span>
                                                <span className="meta-text">{draft.author}</span>
                                            </div>
                                            <div className="meta-item">
                                                <span className="meta-icon">📅</span>
                                                <span className="meta-text">{formatDate(draft.lastUpdated)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Preview Panel */}
                    {selectedDraft && (
                        <div className="preview-panel">
                            <div className="preview-sticky">
                                {/* Preview Header */}
                                <div className="preview-header">
                                    <h2 className="preview-title">Preview</h2>
                                    <div className={`content-type-badge-large ${getContentTypeColor(selectedDraft.contentType)}`}>
                                        <span className="type-icon">{getContentTypeIcon(selectedDraft.contentType)}</span>
                                        <span className="type-label">{selectedDraft.contentType}</span>
                                    </div>
                                </div>

                                {/* Preview Content */}
                                <div className="preview-content">
                                    {/* Main Content */}
                                    <div className="preview-section">
                                        <h3 className="draft-display-title">{selectedDraft.title}</h3>

                                        <div className="draft-meta-info">
                                            <span className="meta-item">
                                                <strong>Created By:</strong> {selectedDraft.author}
                                            </span>
                                            <span className="meta-item">
                                                <strong>Created:</strong> {formatDateTime(selectedDraft.createdDate)}
                                            </span>
                                            {selectedDraft.modifiedBy && selectedDraft.modifiedBy !== selectedDraft.author && (
                                                <span className="meta-item">
                                                    <strong>Modified By:</strong> {selectedDraft.modifiedBy}
                                                </span>
                                            )}
                                            <span className="meta-item">
                                                <strong>Last Updated:</strong> {formatDateTime(selectedDraft.lastUpdated)}
                                            </span>
                                            <span className="meta-item">
                                                <strong>Category:</strong> {getCategoryName(selectedDraft.categoryId)}
                                            </span>
                                        </div>

                                        <div className="content-block">
                                            <label className="content-label">Short Description</label>
                                            <p className="content-text">
                                                {selectedDraft.shortDescription || '—'}
                                            </p>
                                        </div>

                                        <div className="content-block">
                                            <label className="content-label">Full Overview</label>
                                            <div className="content-text content-full">
                                                {selectedDraft.fullContent || '—'}
                                            </div>
                                        </div>

                                        {selectedDraft.highlights && (
                                            <div className="content-block">
                                                <label className="content-label">Highlights</label>
                                                <div className="content-text">
                                                    {selectedDraft.highlights}
                                                </div>
                                            </div>
                                        )}

                                        {/* Features Section */}
                                        {selectedDraft.completeData?.features && selectedDraft.completeData.features.length > 0 && (
                                            <div className="content-block">
                                                <label className="content-label">Features ({selectedDraft.completeData.features.length})</label>
                                                <div className="features-list">
                                                    {selectedDraft.completeData.features.map((feature, idx) => (
                                                        <div key={idx} className="feature-item">
                                                            <strong>⚡ {feature.featureName}</strong>
                                                            {feature.featureDescription && (
                                                                <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                                                                    {feature.featureDescription}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Plans Section */}
                                        {selectedDraft.completeData?.plans && selectedDraft.completeData.plans.length > 0 && (
                                            <div className="content-block">
                                                <label className="content-label">Plans ({selectedDraft.completeData.plans.length})</label>
                                                <div className="plans-list">
                                                    {selectedDraft.completeData.plans.map((plan, idx) => (
                                                        <div key={idx} className="plan-item">
                                                            <strong>💰 {plan.planName}</strong>
                                                            <p style={{ marginTop: '4px', fontSize: '14px' }}>
                                                                Amount: {plan.planAmountType === 'R' ? '₹' : '$'}{plan.planAmount}
                                                                {plan.planDescription && ` - ${plan.planDescription}`}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Specifications Section */}
                                        {selectedDraft.completeData?.specifications && selectedDraft.completeData.specifications.length > 0 && (
                                            <div className="content-block">
                                                <label className="content-label">Specifications ({selectedDraft.completeData.specifications.length})</label>
                                                <div className="specifications-list">
                                                    {selectedDraft.completeData.specifications.map((spec, idx) => (
                                                        <div key={idx} className="specification-item">
                                                            <strong>📋 {spec.specificationName}</strong>
                                                            {spec.specificationValue && (
                                                                <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                                                                    {spec.specificationValue}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Support Section */}
                                        {selectedDraft.completeData?.support && selectedDraft.completeData.support.length > 0 && (
                                            <div className="content-block">
                                                <label className="content-label">Support Options ({selectedDraft.completeData.support.length})</label>
                                                <div className="support-list">
                                                    {selectedDraft.completeData.support.map((supportItem, idx) => (
                                                        <div key={idx} className="support-item">
                                                            <strong>🛟 {supportItem.supportType}</strong>
                                                            {supportItem.supportDescription && (
                                                                <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>
                                                                    {supportItem.supportDescription}
                                                                </p>
                                                            )}
                                                            {supportItem.supportContact && (
                                                                <p style={{ marginTop: '4px', fontSize: '13px', color: '#0066cc' }}>
                                                                    Contact: {supportItem.supportContact}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Approve Button */}
                                    <div className="preview-actions">
                                        <button
                                            className="btn-approve"
                                            onClick={handleApprove}
                                            disabled={approvedDraftId === selectedDraft.id || loading}
                                        >
                                            <span className="btn-icon">✓</span>
                                            {approvedDraftId === selectedDraft.id ? 'Approved' : 'Approve & Publish'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <SecondaryFooter />
        </div>
        </AdminLayout>
    );
};

export default AdminDraftReview;